import { useRef, useState } from 'react'
import { Bug, ImagePlus, X } from 'lucide-react'
import { toast } from 'sonner'
import { Drawer } from '@/components/primitives/Drawer'
import { api } from '@/lib/api/client'

interface Props {
  open: boolean
  onClose: () => void
}

const SEVERITIES = ['Low', 'Medium', 'High', 'Critical'] as const

export default function FeedbackDrawer({ open, onClose }: Props) {
  const [title,       setTitle]       = useState('')
  const [description, setDescription] = useState('')
  const [steps,       setSteps]       = useState('')
  const [severity,    setSeverity]    = useState<string>('Medium')
  const [screenshot,  setScreenshot]  = useState<string | null>(null)
  const [screenshotName, setScreenshotName] = useState('')
  const [submitting,  setSubmitting]  = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Screenshot must be under 5 MB')
      return
    }
    setScreenshotName(file.name)
    const reader = new FileReader()
    reader.onload = () => setScreenshot(reader.result as string)
    reader.readAsDataURL(file)
  }

  const removeScreenshot = () => {
    setScreenshot(null)
    setScreenshotName('')
    if (fileRef.current) fileRef.current.value = ''
  }

  const reset = () => {
    setTitle('')
    setDescription('')
    setSteps('')
    setSeverity('Medium')
    setScreenshot(null)
    setScreenshotName('')
    if (fileRef.current) fileRef.current.value = ''
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    setSubmitting(true)
    try {
      const res = await api.post<{ wiId: number; title: string }>('/feedback/bugs', {
        title,
        description,
        stepsToReproduce: steps,
        pageUrl: window.location.href,
        severity,
        screenshot: screenshot ?? undefined,
      })
      toast.success(`Bug reported — WI #${res.wiId}`)
      handleClose()
    } catch {
      toast.error('Failed to submit bug report. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Drawer open={open} onClose={handleClose}>
      <div className="drawer-header">
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <Bug size={16} style={{ color: 'var(--ns-brand)' }} />
          <span style={{ fontWeight: 600, fontSize: 14 }}>Report a bug</span>
        </div>
        <button className="btn btn-ghost btn-icon btn-sm" onClick={handleClose}>
          <X size={14} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="drawer-body col" style={{ gap: 16 }}>
        {/* Title */}
        <div className="col" style={{ gap: 4 }}>
          <label className="label" htmlFor="fb-title">
            Title <span style={{ color: 'var(--ns-danger)' }}>*</span>
          </label>
          <input
            id="fb-title"
            className="input"
            placeholder="Short description of the issue"
            value={title}
            onChange={e => setTitle(e.target.value)}
            required
            maxLength={200}
          />
        </div>

        {/* Description */}
        <div className="col" style={{ gap: 4 }}>
          <label className="label" htmlFor="fb-desc">What happened?</label>
          <textarea
            id="fb-desc"
            className="input"
            style={{ minHeight: 72, resize: 'vertical' }}
            placeholder="Describe what you saw vs. what you expected"
            value={description}
            onChange={e => setDescription(e.target.value)}
            maxLength={2000}
          />
        </div>

        {/* Steps */}
        <div className="col" style={{ gap: 4 }}>
          <label className="label" htmlFor="fb-steps">Steps to reproduce</label>
          <textarea
            id="fb-steps"
            className="input"
            style={{ minHeight: 72, resize: 'vertical' }}
            placeholder="1. Go to…&#10;2. Click…&#10;3. See error"
            value={steps}
            onChange={e => setSteps(e.target.value)}
            maxLength={2000}
          />
        </div>

        {/* Severity + screenshot row */}
        <div className="row" style={{ gap: 12, alignItems: 'flex-end' }}>
          <div className="col" style={{ gap: 4, flex: '0 0 auto' }}>
            <label className="label" htmlFor="fb-severity">Severity</label>
            <select
              id="fb-severity"
              className="input"
              style={{ width: 120 }}
              value={severity}
              onChange={e => setSeverity(e.target.value)}
            >
              {SEVERITIES.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div className="col" style={{ gap: 4, flex: 1 }}>
            <label className="label">Screenshot (optional)</label>
            {screenshot ? (
              <div className="row" style={{ gap: 6, alignItems: 'center' }}>
                <img
                  src={screenshot}
                  alt="preview"
                  style={{ height: 32, width: 'auto', borderRadius: 4, border: '1px solid var(--ns-border)' }}
                />
                <span className="muted" style={{ fontSize: 11, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {screenshotName}
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-icon btn-sm"
                  onClick={removeScreenshot}
                  title="Remove screenshot"
                >
                  <X size={12} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ justifyContent: 'flex-start', gap: 6 }}
                onClick={() => fileRef.current?.click()}
              >
                <ImagePlus size={14} />
                Attach image
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFile}
            />
          </div>
        </div>

        {/* Page URL note */}
        <p className="muted" style={{ fontSize: 11, margin: 0 }}>
          Current page URL will be included automatically.
        </p>

        <div className="drawer-footer">
          <button type="button" className="btn btn-ghost btn-sm" onClick={handleClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary btn-sm"
            disabled={submitting || !title.trim()}
          >
            {submitting ? 'Submitting…' : 'Submit bug report'}
          </button>
        </div>
      </form>
    </Drawer>
  )
}
